import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUndoableDelete } from "@/hooks/use-undoable-delete";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { configKeys } from "@/lib/query";
import {
  type CancelReason,
  cancelReasonArraySchema,
  cancelReasonSchema,
  createCancelReasonSchema,
} from "@/schemas/config";

// Inline-row form shape. Both create and update use the same required-field
// validation — partial updates aren't useful here because the row only has
// two fields and the user always sees both in the editor at once.
type FormValues = z.infer<typeof createCancelReasonSchema>;

const emptyForm: FormValues = {
  cancelReasonCode: "",
  cancelReason: "",
};

// Sub-component for the editable row. Owns its own react-hook-form instance
// so each Save/Cancel cycle starts with a fresh, validated form — no manual
// state reset, no stale errors leaking between edits.
//
// The row sits inside <tbody>, where a wrapping <form> element is invalid
// HTML, so the Save button drives submission by calling
// form.handleSubmit(onValid) directly. Enter inside either Input fires the
// same handler via the onKeyDown wiring below.
function EditRow({
  defaultValues,
  isPending,
  onSubmit,
  onCancel,
}: {
  defaultValues: FormValues;
  isPending: boolean;
  onSubmit: (data: FormValues) => void;
  onCancel: () => void;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(createCancelReasonSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const submit = form.handleSubmit((data) => {
    onSubmit({
      cancelReasonCode: data.cancelReasonCode.trim(),
      cancelReason: data.cancelReason.trim(),
    });
  });

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <Form {...form}>
      <tr className="border-b">
        <td className="py-3 pr-4 align-top">
          <FormField
            control={form.control}
            name="cancelReasonCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Reason code"
                    autoFocus
                    onFocus={(e) => e.currentTarget.select()}
                    onKeyDown={handleKeyDown}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </td>
        <td className="py-3 pr-4 align-top">
          <FormField
            control={form.control}
            name="cancelReason"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input placeholder="Cancel reason" onKeyDown={handleKeyDown} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </td>
        <td className="py-3 align-top">
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void submit()} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    </Form>
  );
}

export function CancelReasonsPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDefaults, setEditingDefaults] = useState<FormValues>(emptyForm);
  const [pendingDelete, setPendingDelete] = useState<CancelReason | null>(null);

  const {
    data: reasons,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: configKeys.cancelReasons,
    queryFn: () => apiGet("/api/config/cancel-reasons", cancelReasonArraySchema),
  });

  const createMutation = useMutation({
    mutationFn: (data: FormValues) =>
      apiSend("POST", "/api/config/cancel-reasons", data, cancelReasonSchema),
    onSuccess: () => {
      toast.success("Cancel reason created");
      queryClient.invalidateQueries({ queryKey: configKeys.cancelReasons });
      closeEditor();
    },
    onError: (e) => {
      logger.error("cancel_reason_create_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to create cancel reason");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormValues }) =>
      apiSend("PUT", `/api/config/cancel-reasons/${id}`, data, cancelReasonSchema),
    onSuccess: () => {
      toast.success("Cancel reason updated");
      queryClient.invalidateQueries({ queryKey: configKeys.cancelReasons });
      closeEditor();
    },
    onError: (e) => {
      logger.error("cancel_reason_update_failed", { error: e });
      toast.error(e instanceof Error ? e.message : "Failed to update cancel reason");
    },
  });

  const { pendingIds, enqueue: enqueueDelete } = useUndoableDelete<CancelReason>({
    describe: (r) => `Deleted "${r.cancelReasonCode} — ${r.cancelReason}"`,
    commit: (r) => apiSend("DELETE", `/api/config/cancel-reasons/${r.id}`, undefined, z.unknown()),
    onCommitSuccess: () => {
      queryClient.invalidateQueries({ queryKey: configKeys.cancelReasons });
    },
    onCommitError: (_, e) => {
      logger.error("cancel_reason_delete_failed", { error: e });
    },
  });

  function handleAdd() {
    setEditingId("new");
    setEditingDefaults(emptyForm);
  }

  function handleEdit(reason: CancelReason) {
    setEditingId(reason.id);
    setEditingDefaults({
      cancelReasonCode: reason.cancelReasonCode,
      cancelReason: reason.cancelReason,
    });
  }

  function closeEditor() {
    setEditingId(null);
    setEditingDefaults(emptyForm);
  }

  function handleSave(data: FormValues) {
    if (editingId === "new") {
      createMutation.mutate(data);
    } else if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Cancel Reasons</h1>
        <div className="mt-4 space-y-2">
          {["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
            <div key={k} className="h-9 animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    logger.error("Failed to load cancel reasons", { error });
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Cancel Reasons</h1>
        <ErrorState message="Couldn't load cancel reasons." onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between rounded-md border bg-card p-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Cancel Reasons</h1>
          <p className="mt-1 text-muted-foreground">Manage policy cancellation reason codes.</p>
        </div>
        <Button onClick={handleAdd} disabled={editingId !== null}>
          <Plus className="size-4" />
          Add Reason
        </Button>
      </div>

      <Card className="mt-6">
        <CardHeader />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 pr-4 font-medium">Reason Code</th>
                  <th className="pb-3 pr-4 font-medium">Cancel Reason</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reasons
                  ?.filter((r) => !pendingIds.has(r.id))
                  .map((reason) =>
                    editingId === reason.id ? (
                      <EditRow
                        key={reason.id}
                        defaultValues={editingDefaults}
                        isPending={isMutating}
                        onSubmit={handleSave}
                        onCancel={closeEditor}
                      />
                    ) : (
                      <tr key={reason.id} className="border-b">
                        <td className="py-3 pr-4 font-mono">{reason.cancelReasonCode}</td>
                        <td className="py-3 pr-4">{reason.cancelReason}</td>
                        <td className="py-3">
                          {/* Wrap each disabled button in a span so the native
                            title tooltip still fires — browsers swallow
                            pointer events on disabled <button>s, so a title
                            attribute on the button alone never shows. */}
                          <div className="flex gap-2">
                            <span
                              title={
                                editingId !== null ? "Finish editing the other row first" : "Edit"
                              }
                              className="inline-block"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label="Edit"
                                onClick={() => handleEdit(reason)}
                                disabled={editingId !== null}
                              >
                                <Pencil className="size-4" />
                              </Button>
                            </span>
                            <span
                              title={
                                editingId !== null ? "Finish editing the other row first" : "Delete"
                              }
                              className="inline-block"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label="Delete"
                                onClick={() => setPendingDelete(reason)}
                                disabled={editingId !== null}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </span>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                {editingId === "new" && (
                  <EditRow
                    key="new"
                    defaultValues={emptyForm}
                    isPending={isMutating}
                    onSubmit={handleSave}
                    onCancel={closeEditor}
                  />
                )}
              </tbody>
            </table>
            {reasons?.length === 0 && editingId !== "new" && (
              <p className="py-8 text-center text-muted-foreground">
                No cancel reasons configured. Click "Add Reason" to create one.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title="Delete cancel reason?"
        description={
          pendingDelete
            ? `This will remove "${pendingDelete.cancelReasonCode} — ${pendingDelete.cancelReason}". You'll have 10 seconds to undo from the toast.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDelete) enqueueDelete(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
